import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SlackChannelPicker from '../components/SlackChannelPicker';
import { connectorGrantsApi, agreementsApi } from '../utils/api';

// PRD-003 WU B-5 — Slack connector setup page.
//
// Flow (real):
//   1. Buyer admin clicks "Connect Slack" → redirected to Slack OAuth.
//   2. Slack redirects back to /agreements/:id/connectors/slack/setup
//      with ?code=…
//   3. Page POSTs the code to the backend (which exchanges it,
//      stores the encrypted token, returns the channel list).
//   4. Page renders SlackChannelPicker; on submit, PUT /scope writes
//      channel_ids and sets grant_expires_at = term_ends_at.
//
// Flow (dev — no real Slack app yet): the user can paste an OAuth
// code manually in the form below; the backend's mocked Slack fetcher
// (in tests) accepts any non-empty string.

const SLACK_OAUTH_BASE = 'https://slack.com/oauth/v2/authorize';
const SLACK_SCOPES = 'channels:history,channels:read,groups:history,groups:read';

export default function ConnectorGrantSetup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [agreement, setAgreement] = useState(null);
  const [grant, setGrant] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [oauthCode, setOauthCode] = useState(searchParams.get('code') || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [aRes, gRes] = await Promise.all([
          agreementsApi.get(id),
          connectorGrantsApi.list(id),
        ]);
        if (cancelled) return;
        setAgreement(aRes.data.agreement || aRes.data);
        const active = (gRes.data.grants || []).find((g) => !g.revoked_at);
        setGrant(active || null);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  async function exchangeCode() {
    if (!oauthCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await connectorGrantsApi.create(id, {
        source_system: 'slack',
        oauth_code: oauthCode.trim(),
      });
      setGrant(r.data.grant);
      setChannels(r.data.channels || []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitChannelScope(channelIds) {
    setBusy(true);
    setError(null);
    try {
      await connectorGrantsApi.setScope(id, grant.id, channelIds);
      navigate(`/agreements/${id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function revokeGrant() {
    if (!grant) return;
    setBusy(true);
    try {
      await connectorGrantsApi.revoke(id, grant.id);
      setGrant(null);
      setChannels([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <Layout><div className="p-6 text-gray-600">Loading…</div></Layout>;
  }

  const clientId = process.env.REACT_APP_SLACK_CLIENT_ID;
  const redirectUri = window.location.origin + `/agreements/${id}/connectors/slack/setup`;
  const slackAuthorizeUrl = clientId
    ? `${SLACK_OAUTH_BASE}?client_id=${encodeURIComponent(clientId)}&scope=${encodeURIComponent(SLACK_SCOPES)}&redirect_uri=${encodeURIComponent(redirectUri)}`
    : null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div>
          <button onClick={() => navigate(`/agreements/${id}`)} className="text-sm text-gray-600 hover:text-gray-700">
            ← Back to Agreement
          </button>
          <h1 className="text-2xl font-semibold mt-2">Connect Slack</h1>
          <p className="text-sm text-gray-600 mt-1">
            Authorize the verification agent to read messages from specific Slack channels
            so it can attest to delivery against this Agreement's Outcome Specs.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {!grant && (
          <div className="card p-4 space-y-3">
            <h2 className="font-semibold text-sm">Step 1 — Authorize the bot</h2>
            {slackAuthorizeUrl ? (
              <a href={slackAuthorizeUrl} className="btn-primary inline-block">
                Connect Slack
              </a>
            ) : (
              <p className="text-xs text-gray-600">
                REACT_APP_SLACK_CLIENT_ID is not configured. Use the manual code-paste path below
                during dev.
              </p>
            )}
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer">Paste OAuth code manually (dev)</summary>
              <div className="mt-3 space-y-2">
                <input
                  className="input text-sm"
                  placeholder="xoxa-…"
                  value={oauthCode}
                  onChange={(e) => setOauthCode(e.target.value)}
                  disabled={busy}
                />
                <button onClick={exchangeCode} disabled={busy || !oauthCode.trim()} className="btn-secondary text-sm">
                  Exchange code
                </button>
              </div>
            </details>
          </div>
        )}

        {grant && channels.length > 0 && (
          <div className="card p-4 space-y-3">
            <h2 className="font-semibold text-sm">Step 2 — Pick channels</h2>
            <SlackChannelPicker
              channels={channels}
              onSubmit={submitChannelScope}
              onCancel={() => navigate(`/agreements/${id}`)}
              busy={busy}
            />
          </div>
        )}

        {grant && channels.length === 0 && (
          <div className="card p-4 space-y-3">
            <h2 className="font-semibold text-sm">Slack connected</h2>
            <p className="text-xs text-gray-600">
              Active grant. Channels:{' '}
              {(grant.scope_params_json?.channel_ids || []).join(', ') || '(none yet)'}
            </p>
            <p className="text-xs text-gray-600">
              Expires {new Date(grant.grant_expires_at).toISOString()}
            </p>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/agreements/${id}`)} className="btn-primary text-sm">
                Done
              </button>
              <button onClick={revokeGrant} disabled={busy} className="btn-secondary text-sm">
                Revoke grant
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
